import type { Ref } from 'react';
import { BookOpen } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import type { FocusMode, FocusView } from '@/lib/focus/focusMachine';
import { formatClock, formatEndTime, formatMinutes } from '@/lib/focus/format';
import { RoundStops } from './RoundStops';
import { SakuraScene } from './sakura/SakuraScene';
import {
  StageActions,
  StageFooter,
  type StageActionHandlers,
} from './StageActions';
import { TimerRing, type RingTone } from './TimerRing';
import type { FocusViewModel } from './focusViewModel';

/** What a just-finished session added up to, for the face. */
export interface FocusStageDone {
  focusMinutes: number;
  blocks: number;
  /** The rounds it was planned with (absent for Flow). */
  rounds?: number;
}

export interface FocusStageProps extends StageActionHandlers {
  view: FocusViewModel;
  /** The set-up's choices while idle; a running session shows its own. */
  setup: { mode: FocusMode; minutes: number; rounds: number; view: FocusView };
  /** "Now", for the end times. */
  now: number;
  /** When the running round ends, or `null` (paused, Flow). */
  endsAt: number | null;
  /** When the whole session ends, or `null` (Flow). */
  sessionEndsAt: number | null;
  /** A finished session: the face shows what it added up to. */
  done?: FocusStageDone | null;
  /** Inside focus mode (the classic view's full-screen column). */
  immersive?: boolean;
  /** The sakura box hands its live scene to focus mode while that is open. */
  sceneActive?: boolean;
  sceneBoxRef?: Ref<HTMLDivElement>;
  onEnterFocusMode?: () => void;
  focusReturn?: { pending: boolean; done: () => void };
}

type FaceProps = Pick<FocusStageProps, 'view' | 'setup' | 'done'>;

/** Which view the stage draws: the set-up's choice before Start, the session's after. */
export function stageView(p: FaceProps): FocusView {
  const idle = p.view.phase === 'idle' && !p.view.between && !p.done;
  const mode = idle ? p.setup.mode : p.view.mode;
  if (mode === 'flow') return 'classic';
  return idle ? p.setup.view : p.view.view;
}

/** "Focus · round 2 of 4", "Long break · after round 2", "Ready · 4 rounds". */
export function phaseText(p: FaceProps): string {
  const { view } = p;
  if (p.done)
    return `${p.done.blocks} ${p.done.blocks === 1 ? 'round' : 'rounds'}, done`;
  const total = view.round?.total ?? p.setup.rounds;
  if (view.between)
    return `Ready · round ${view.round?.current ?? 1} of ${total}`;
  if (view.phase === 'idle') {
    if (p.setup.mode === 'flow') return 'Counts up';
    return `Ready · ${p.setup.rounds} ${p.setup.rounds === 1 ? 'round' : 'rounds'}`;
  }
  if (view.phase === 'break') {
    const after = view.round ? ` · after round ${view.round.current}` : '';
    return `${view.breakKind === 'long' ? 'Long break' : 'Break'}${after}`;
  }
  const label =
    view.phase === 'paused'
      ? 'Paused'
      : view.progress === null
        ? 'In flow'
        : 'Focus';
  return view.round
    ? `${label} · round ${view.round.current} of ${total}`
    : label;
}

/** The time on the face: the length set up (or about to start), or the clock. */
export function faceTime(p: FaceProps): string {
  if (p.done) return formatMinutes(p.done.focusMinutes * 60_000);
  if (p.view.phase !== 'idle') return p.view.timeLabel;
  const mode = p.view.between ? p.view.mode : p.setup.mode;
  return formatClock(mode === 'flow' ? 0 : p.setup.minutes * 60_000);
}

function Caption({
  view,
  endsAt,
  sessionEndsAt,
  now,
}: Pick<FocusStageProps, 'view' | 'endsAt' | 'sessionEndsAt' | 'now'>) {
  if (view.phase !== 'focus' && view.phase !== 'paused') return null;
  const session = sessionEndsAt
    ? ` · session ends ${formatEndTime(now, sessionEndsAt)}`
    : '';
  const when =
    endsAt !== null
      ? `Ends at ${formatEndTime(now, endsAt)}${session}`
      : view.phase === 'paused'
        ? 'Paused. The clock waits for you (up to 30 min).'
        : 'In flow. Stop whenever you naturally do.';
  return (
    <div className='flex max-w-md flex-col items-center gap-1 text-center'>
      <p
        className='flex max-w-full items-center gap-2 text-lg font-medium text-foreground'
        title={view.intention}
      >
        {view.lessonId && (
          <BookOpen className='h-4 w-4 shrink-0' aria-hidden='true' />
        )}
        <span className='truncate'>
          {view.intention ?? 'One thing at a time.'}
        </span>
      </p>
      <p className='font-mono text-xs text-muted-foreground'>{when}</p>
    </div>
  );
}

function ringTone(view: FocusViewModel, done: boolean): RingTone {
  if (done || view.phase === 'break') return 'rest';
  if (view.phase === 'paused') return 'paused';
  if (view.phase === 'focus' && view.progress === null) return 'flow';
  return 'focus';
}

/** Said once when the phase changes, for anyone not watching the face. */
function announcement(view: FocusViewModel): string {
  if (view.phase === 'focus') return 'Focus round running.';
  if (view.phase === 'paused') return 'Timer paused.';
  if (view.phase === 'break')
    return view.breakKind === 'long' ? 'Long break started.' : 'Break started.';
  if (view.between) return 'Break over.';
  return '';
}

function ClassicFace(p: FocusStageProps) {
  const { view } = p;
  const done = Boolean(p.done);
  const idle = view.phase === 'idle' && !view.between && !done;
  const running =
    view.phase === 'focus' || view.phase === 'paused' || view.phase === 'break';
  const fraction = running && view.progress !== null ? 1 - view.progress : 1;
  const time = faceTime(p);
  const tag = done
    ? null
    : view.round
      ? `Round ${view.round.current} of ${view.round.total}`
      : idle && p.setup.mode === 'timed'
        ? `Round 1 of ${p.setup.rounds}`
        : null;
  const label =
    done || !idle
      ? phaseText(p).split(' · ')[0]
      : p.setup.mode === 'flow'
        ? 'Counts up'
        : 'Ready';
  return (
    <TimerRing
      fraction={fraction}
      tone={ringTone(view, done)}
      className={
        p.immersive ? 'size-[min(22rem,46vh)]' : 'size-[min(17rem,34vh)]'
      }
    >
      {tag && (
        <span className='font-mono text-xs uppercase tracking-wider text-muted-foreground'>
          {tag}
        </span>
      )}
      <span
        className={cn(
          'whitespace-nowrap font-mono font-semibold tabular-nums tracking-tight text-foreground',
          done || time.length > 5
            ? 'text-3xl sm:text-4xl'
            : p.immersive
              ? 'text-6xl sm:text-7xl'
              : 'text-5xl sm:text-6xl'
        )}
      >
        {time}
      </span>
      <span className='text-sm font-medium text-muted-foreground'>
        {done ? phaseText(p) : label}
      </span>
    </TimerRing>
  );
}

/** What the sakura reward is called, and what it says. */
export function sakuraReward(done: FocusStageDone) {
  const full = done.rounds === undefined || done.blocks >= done.rounds;
  const rounds = `${done.blocks} ${done.blocks === 1 ? 'round' : 'rounds'}`;
  return {
    title: full
      ? 'Your sakura, in full bloom'
      : 'Your sakura, coming into bloom',
    detail: `${formatMinutes(done.focusMinutes * 60_000)} of focus over ${rounds}`,
  };
}

/** The sakura view on the page: the whole scene in its box, the time on it. */
function SakuraBox(p: FocusStageProps) {
  const label = p.done ? sakuraReward(p.done) : null;
  return (
    <div
      ref={p.sceneBoxRef}
      className='relative min-h-[12rem] w-full flex-1 overflow-hidden rounded-2xl shadow-elevation-1'
    >
      <SakuraScene
        progress={p.view.sakura}
        phase={p.view.phase}
        active={p.sceneActive !== false}
      />
      {label ? (
        <div className='absolute left-1/2 top-3 flex -translate-x-1/2 flex-col items-center gap-0.5 whitespace-nowrap rounded-xl bg-card/75 px-4 py-2 text-center backdrop-blur-md'>
          <b className='text-base font-semibold text-foreground'>
            {label.title}
          </b>
          <span className='text-xs text-muted-foreground'>{label.detail}</span>
        </div>
      ) : (
        <div className='absolute left-3 top-3 inline-flex items-baseline gap-2.5 rounded-full bg-card/75 px-4 py-1.5 backdrop-blur-md'>
          <span className='font-mono text-2xl font-semibold tabular-nums text-foreground'>
            {faceTime(p)}
          </span>
          <span className='text-xs font-medium text-muted-foreground'>
            {phaseText(p)}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * The timer: the classic ring, or the sakura scene in its box, with what the
 * round is for and the one action that matters now. Keeping it in one place
 * is what lets Start, Pause and the break happen without anything jumping.
 */
export function FocusStage(p: FocusStageProps) {
  const { view } = p;
  const done = Boolean(p.done);
  const sakura = stageView(p) === 'sakura';
  const running = view.phase === 'focus' || view.phase === 'paused';
  // Setting up on a phone, the face is not drawn: the plan above already says
  // "25 min, 4 rounds", and an empty "25:00 Ready" ring under it was half a
  // screen of repetition. Start is pinned to the bottom there (see
  // `StageActions`), so nothing is lost; the face appears with the round.
  const settingUp = view.phase === 'idle' && !view.between && !done;
  const face = settingUp ? 'contents max-sm:hidden' : 'contents';
  const showStops =
    !sakura &&
    view.mode === 'timed' &&
    view.round &&
    (view.phase !== 'idle' || view.between);
  return (
    <section
      aria-label='Timer'
      className={cn(
        'flex min-h-0 flex-col items-center gap-4',
        sakura ? 'h-full w-full' : 'justify-center py-2'
      )}
    >
      <div className={face}>
        {sakura ? <SakuraBox {...p} /> : <ClassicFace {...p} />}
      </div>
      <p className='sr-only' aria-live='polite'>
        {announcement(view)}
      </p>
      <div className={face}>
        <Caption
          view={view}
          endsAt={p.endsAt}
          sessionEndsAt={p.sessionEndsAt}
          now={p.now}
        />
      </div>
      {showStops && view.round && (
        <RoundStops
          rounds={view.round.total}
          done={
            view.phase === 'break' ? view.round.current : view.round.current - 1
          }
          at={running ? 'round' : view.phase === 'break' ? 'break' : 'idle'}
          current={running && view.progress !== null ? view.progress : 0}
        />
      )}
      <StageActions
        view={view}
        done={done}
        onStart={p.onStart}
        onPause={p.onPause}
        onResume={p.onResume}
        onFinish={p.onFinish}
        onNextBlock={p.onNextBlock}
        onEndSession={p.onEndSession}
      />
      <StageFooter
        view={view}
        done={done}
        immersive={p.immersive}
        onEnterFocusMode={p.onEnterFocusMode}
        focusReturn={p.focusReturn}
      />
    </section>
  );
}
